/*
 * business-card/presets.js — the layouts, and why they are functions.
 *
 * WHAT A "TEMPLATE" USUALLY IS, AND WHY THAT FAILS HERE
 * ────────────────────────────────────────────────────
 * Every competitor ships templates as fixed artwork: elements pinned at
 * absolute pixel positions for one card size, in one language, with one set of
 * fields filled in. That works right up until reality touches it —
 *
 *   - the card is 85 × 55 and the template was drawn for 90 × 50;
 *   - the user has no logo, and the template has a hole where it was;
 *   - the job title is "Заместитель генерального директора по развитию" and
 *     the slot was measured against "CEO";
 *   - there is no second phone, and the block below it does not move up.
 *
 * A fixed template answers all four by overlapping, clipping, or leaving a gap.
 * So a preset here is a FUNCTION of (geometry, data, palette): it is handed the
 * safe box for whatever format is active and only the fields that actually have
 * content, and it returns the elements. Nothing is positioned that does not
 * exist, and everything reflows.
 *
 * That is also what makes the format switcher honest. Changing RU 90×50 to
 * EU 85×55 re-runs the preset against the new box instead of stretching
 * artwork, so type stays at its designed physical size and only the space
 * around it changes — which is what a designer would do by hand.
 *
 * OUTPUT CONTRACT
 * ───────────────
 * Elements are millimetres, origin at the TRIM top-left, y DOWN. Never the
 * bleed box: see spec.js for why that choice keeps a layout stable when the
 * bleed changes. `bleed: true` on a rect means "extend past the trim to the
 * page edge" and is how a full-colour background is expressed — the exporter
 * grows it, the preview crops it, and neither has to know about the other.
 *
 * Colours are ROLE NAMES ('ink', 'accent', 'muted', 'paper'), resolved against
 * the active palette by the caller. A preset never names a colour, which is
 * what lets any palette be dropped onto any layout without either knowing.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CardPresets = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* ── type scale, in millimetres ──────────────────────────────────────────
     * Sizes are physical, not relative to the card: a name set at 4.2 mm is
     * 4.2 mm on a 90 mm card and on a 55 mm square one. Scaling type with the
     * card is the classic mistake — it makes a small card unreadable and a
     * large one shouty, when what actually changes is the amount of air.
     *
     * The floor is anchored to legibility rather than taste. 2.1 mm em is
     * about 6 pt, which is where small print stops being print; contact lines
     * sit at 2.6-2.9 mm (7.5-8 pt), the size a phone number is set at on
     * essentially every professionally produced card.
     */
    var T = {
        name:    4.4,
        nameSm:  3.8,
        role:    2.7,
        company: 3.2,
        contact: 2.7,
        tiny:    2.5,
        tagline: 2.5,
        mono:    12,
    };

    /* Contact icons, authored as STROKE paths in a 24×24 box.
     *
     * Stroke rather than fill, and consistently so, because the two backends
     * only agree if the intent is unambiguous. The first version mixed the two
     * — a filled handset next to an envelope whose flap was a stroke — and
     * filling all of them turned the globe into a blob and the phone into a
     * speck, while the canvas preview (which also filled them) looked different
     * enough that the mismatch was only visible in a rendered PDF. Line icons
     * also sit better next to 7 pt type than heavy filled glyphs.
     *
     * ICON_STROKE is in the same 24-unit space as the paths; at a 2.65 mm icon
     * it works out to 0.19 mm on paper, comfortably above the 0.09 mm the
     * plate can hold.
     */
    var ICON_STROKE = 1.7;

    /* ⚠️ ARC FLAGS ARE SPACE-SEPARATED HERE ON PURPOSE.
     * SVG allows the two flags of an elliptical arc to be written without
     * separators — `a9 9 0 1 0 0 18` may be compressed to `a9 9 0 100 18` — and
     * every icon set on the internet ships the compressed form. Browsers parse
     * it; pdf-lib does NOT. Measured 2026-08-23: the same arc emits 6 path
     * operators spaced and 4 compressed, so the compressed globe and pin drew a
     * blob and a leaf IN THE PDF ONLY, while the canvas preview was perfect.
     * That is the worst shape a bug can have here — the preview is the thing
     * the user checks. Any icon pasted in from elsewhere must be re-spaced.
     * Guard: tests/playwright/business-card-pdf.mjs renders each icon and
     * compares its ink against the canvas.
     */
    var ICONS = {
        phone: 'M5 4h4l2 5-2.5 1.5a12 12 0 0 0 5 5L15 13l5 2v4a1.5 1.5 0 0 1-1.6 1.5C10 19.8 4.2 14 3.5 5.6A1.5 1.5 0 0 1 5 4z',
        mail:  'M3 6h18v12H3zM3 6.5l9 6.5 9-6.5',
        globe: 'M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18zM3 12h18M12 3c2.3 2.5 3.5 5.7 3.5 9s-1.2 6.5-3.5 9c-2.3-2.5-3.5-5.7-3.5-9S9.7 5.5 12 3z',
        pin:   'M12 21s7-6.6 7-11.5A7 7 0 0 0 5 9.5C5 14.4 12 21 12 21zM12 12a2.5 2.5 0 1 0 0-5a2.5 2.5 0 1 0 0 5z',
        tg:    'M21 4L3 10.5l5 1.8L18 7l-8 6.6.3 5L13 15l5 3.5z',
        wa:    'M12 3a9 9 0 0 0-7.6 13.8L3 21l4.4-1.3A9 9 0 1 0 12 3zM9 8.5c.2-.5.4-.5.7-.5h.5l1 2.3-.8.9c-.1.2-.2.4 0 .7.6 1.2 1.7 2.3 2.9 2.9.3.1.5 0 .7-.2l.9-.8 2.3 1v.5c0 .3 0 .5-.5.7-.6.3-1.6.4-2.6 0-2.5-1-4.5-3-5.5-5.5-.4-1-.3-2 0-2.6z',
    };

    /* ── little builders ─────────────────────────────────────────────────── */
    function rect(x, y, w, h, color, opt) {
        return assign({ type: 'rect', x: x, y: y, w: w, h: h, color: color }, opt);
    }
    function text(x, y, w, value, size, opt) {
        return assign({
            type: 'text', x: x, y: y, w: w, text: value,
            size: size, color: 'ink', align: 'left', weight: 400,
            lineHeight: 1.22, track: 0, maxLines: 2,
        }, opt);
    }
    function line(x, y, w, color, weight) {
        return { type: 'line', x: x, y: y, w: w, h: 0, color: color, stroke: weight || 0.25 };
    }
    function assign(t) {
        for (var i = 1; i < arguments.length; i++) {
            var s = arguments[i];
            if (!s) continue;
            for (var k in s) if (Object.prototype.hasOwnProperty.call(s, k)) t[k] = s[k];
        }
        return t;
    }

    /* Which contact lines exist, in the order a card reads them. Built from the
     * data so a preset never positions an empty row — the reflow that fixed
     * templates cannot do. */
    function contactRows(d) {
        var rows = [];
        if (d.phone)    rows.push({ icon: 'phone', text: d.phone });
        if (d.phone2)   rows.push({ icon: 'phone', text: d.phone2 });
        if (d.email)    rows.push({ icon: 'mail',  text: d.email });
        if (d.site)     rows.push({ icon: 'globe', text: d.site });
        if (d.telegram) rows.push({ icon: 'tg',    text: d.telegram });
        if (d.whatsapp) rows.push({ icon: 'wa',    text: d.whatsapp });
        if (d.address)  rows.push({ icon: 'pin',   text: d.address, wrap: true });
        return rows;
    }

    /* Stack contact rows. Used by most presets, so the spacing rhythm is
     * identical across them — a card where the contact block is set differently
     * on every template looks like a template gallery, not a brand.
     *
     * `bottom` anchors the block so the LAST LINE'S INK ends there, which is
     * what a preset actually means by "put the contacts at the bottom of the
     * safe area". Anchoring on the last BASELINE instead — the obvious version,
     * and the one this shipped with — leaves the descenders of «р», «у» and «ф»
     * hanging outside the safe box, and the address row wraps to a second line
     * that is not counted at all. Preflight caught both on the default card.
     */
    function contactBlock(rows, o) {
        var out = [];
        var size = o.size || T.contact;
        var step = size * 1.62;
        var iconW = o.icons ? size * 1.05 : 0;
        var gap = o.icons ? size * 0.55 : 0;
        var textW = o.w - iconW - gap;

        /* An address wraps by nature; a long email or a long site wraps because
         * the column is narrow. Allowing two lines and then MEASURING how many
         * are actually used is what keeps the stack honest — reserving two for
         * everything wastes half the card, reserving one for everything
         * overlaps. */
        var metrics = rows.map(function (r) {
            var maxLines = r.wrap ? 3 : 2;
            var m = o.fit
                ? o.fit(r.text, textW, size, maxLines, o.weight || 400)
                : { size: size, lines: 1, lineHeight: size * 1.22 };
            return { row: r, lines: m.lines, size: m.size, lineHeight: m.lineHeight, maxLines: maxLines };
        });

        /* Advance from one row's first baseline to the next: the lines this row
         * actually occupies, plus the gap between rows. */
        function advance(m) { return m.lineHeight * (m.lines - 1) + step; }

        var y = o.y;
        if (o.bottom != null) {
            var adv = 0;
            for (var q = 0; q < metrics.length - 1; q++) adv += advance(metrics[q]);
            var last = metrics[metrics.length - 1];
            var lastInk = last ? last.lineHeight * (last.lines - 1) + last.size * 0.26 : 0;
            y = o.bottom - adv - lastInk;
        }
        var start = y;
        for (var i = 0; i < metrics.length; i++) {
            var m = metrics[i], r = m.row;
            if (o.icons) {
                out.push({
                    type: 'icon', path: ICONS[r.icon], x: o.x, y: y - size * 0.78,
                    size: size * 0.98, color: o.iconColor || 'accent',
                    stroke: (ICON_STROKE / 24) * size * 0.98,
                });
            }
            out.push(text(o.x + iconW + gap, y, textW, r.text, size, {
                color: o.color || 'ink', align: o.align || 'left',
                baseline: true, maxLines: m.maxLines, weight: o.weight || 400,
            }));
            y += advance(m);
        }
        return { elements: out, top: start, height: y - start };
    }

    /* A one-line contact summary can carry three channels before it stops being
     * a line and becomes a paragraph. Which three is not arbitrary: a phone, a
     * way to write, and a place to look — the second phone, the messengers and
     * the postal address all repeat one of those. Presets that run contacts as
     * a stack keep all of them; only the joined-into-one-line layouts trim.
     */
    function summaryContacts(rows) {
        var want = ['phone', 'mail', 'globe'];
        var out = [];
        want.forEach(function (icon) {
            for (var i = 0; i < rows.length; i++) {
                if (rows[i].icon === icon && !rows[i].wrap) { out.push(rows[i].text); return; }
            }
        });
        return out;
    }

    /* ── the logo slot every layout owes the user ────────────────────────────
     * A preset that has no place for a logo makes the upload button do nothing
     * at all — the file loads, the preview does not change, and there is no
     * explanation. Six of the twelve layouts shipped that way until it was
     * measured. So a layout with no natural home for a mark gets a corner slot,
     * top-right, opposite whichever block leads the card.
     *
     * The width is capped as well as the height: a wordmark 6:1 wide asked to
     * be 8 mm tall is 48 mm across and would run into the name.
     */
    function cornerLogoW(g, o) {
        var h = Math.min(g.sh * 0.22, 8);
        return Math.min(h * o.logoRatio, g.sw * 0.34);
    }
    function cornerLogo(g, o, opts) {
        opts = opts || {};
        var h = Math.min(g.sh * 0.22, 8);
        return {
            type: 'logo', x: opts.x != null ? opts.x : g.sx + g.sw, y: opts.y != null ? opts.y : g.sy,
            h: h, align: opts.align || 'right', maxW: g.sw * 0.34, onColor: !!opts.onColor,
        };
    }

    /* Initials for the monogram presets. Two letters, upper-cased, taken from
     * the name — and from the COMPANY when there is no personal name, because a
     * company card with "ОО" on it is worse than one with nothing.
     *
     * ⚠️ `role: 'monogram'` at every call site is what gives the block a STABLE
     * name (`t#monogram`) and a human one in the selection toolbar. Without it
     * the initials were the one thing on the card that was neither a data field
     * nor a nameable element: clicking them offered "Текст" and no way to
     * change them, and double-clicking did nothing at all — silently, which is
     * the worst answer an interface can give. Reported as
     * «там где ВЕ я почему то не могу отредактировать это». */
    function initials(d) {
        var src = (d.name || d.company || '').trim();
        var parts = src.split(/\s+/).filter(Boolean);
        if (!parts.length) return '';
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }

    /* ── the presets ─────────────────────────────────────────────────────────
     * Each receives:
     *   g     — { w, h, safe, sx, sy, sw, sh }  the trim box and its safe inset
     *   d     — the card data, only fields with content
     *   opt   — { hasLogo, logoRatio, hasQr, qrSize, side }
     * and returns an array of elements.
     */
    var LIST = [];
    function preset(id, tags, fn) { LIST.push({ id: id, tags: tags || [], build: fn }); }

    /* 1. The default. Name and role top-left, contacts bottom-left, an accent
     *    rule between them. It is the layout most printed cards actually use,
     *    which is why it is first: the opening screen should look like a card
     *    someone would hand you, not like a demo of the tool. */
    preset('classic', ['simple', 'safe'], function (g, d, o) {
        var e = [];
        var y = g.sy + T.name * 0.9;
        var textW = o.hasLogo ? g.sw - cornerLogoW(g, o) - 3 : g.sw;
        if (o.hasLogo) e.push(cornerLogo(g, o));
        if (d.company) {
            e.push(text(g.sx, y - T.name * 0.9 + T.tiny * 0.85, textW, d.company, T.tiny,
                { color: 'accent', weight: 700, track: 0.09, upper: true, baseline: true, maxLines: 2 }));
            y += T.tiny * 1.5;
        }
        e.push(text(g.sx, y, textW, d.name, T.name, { weight: 700, baseline: true, maxLines: 2 }));
        if (d.role) e.push(text(g.sx, y + T.role * 1.55, g.sw, d.role, T.role,
            { color: 'ink', opacity: 0.62, baseline: true, maxLines: 2 }));

        var rows = contactRows(d);
        var cb = contactBlock(rows, {
            fit: o.fit,
            x: g.sx, bottom: g.sy + g.sh,
            w: o.hasQr ? g.sw - o.qrSize - 3 : g.sw, icons: true,
        });
        /* The rule sits above whatever the block turned out to be, so it moves
         * with the contacts instead of being positioned by a duplicate copy of
         * the block's own arithmetic. */
        e.push(line(g.sx, cb.top - T.contact * 2.1, Math.min(14, g.sw * 0.2), 'accent', 0.5));
        return e.concat(cb.elements);
    });

    /* 2. Centred. The safest layout for a card with a logo and few fields. */
    preset('centered', ['simple', 'logo'], function (g, d, o) {
        var e = [];
        var cx = g.sx + g.sw / 2;
        var top = g.sy;
        if (o.hasLogo) {
            var lh = Math.min(g.sh * 0.3, 11);
            e.push({ type: 'logo', x: cx, y: top, h: lh, align: 'center' });
            top += lh + 2.4;
        }
        e.push(text(g.sx, top + T.name * 0.85, g.sw, d.name, T.name,
            { weight: 700, align: 'center', baseline: true, maxLines: 2 }));
        var y = top + T.name * 0.85;
        if (d.role) { y += T.role * 1.6; e.push(text(g.sx, y, g.sw, d.role, T.role, { color: 'ink', opacity: 0.62, align: 'center', baseline: true })); }
        if (d.company) { y += T.tiny * 1.5; e.push(text(g.sx, y, g.sw, d.company, T.tiny, { color: 'accent', align: 'center', weight: 700, track: 0.08, upper: true, baseline: true, maxLines: 2 })); }

        var rows = contactRows(d);
        var joined = summaryContacts(rows);
        var by = g.sy + g.sh;
        if (joined.length) {
            e.push(line(cx - Math.min(16, g.sw * 0.22) / 2, by - T.contact * 2.6, Math.min(16, g.sw * 0.22), 'muted', 0.3));
            e.push(text(g.sx, by, g.sw, joined.join('   ·   '), T.contact,
                { align: 'center', anchor: 'bottom', maxLines: 3, shrinkOnly: true }));
        }
        return e;
    });

    /* 3. Colour band down the left edge — the most common "designed" card.
     *    The band is a bleed element: it must reach the paper edge or the cut
     *    leaves a white sliver against it, which is the failure this whole tool
     *    is built to prevent. */
    preset('band-left', ['color', 'logo'], function (g, d, o) {
        var bw = Math.max(9, g.w * 0.3);
        var e = [rect(0, 0, bw, g.h, 'accent', { bleed: 'ltb' })];
        var innerX = bw + g.safe;
        var innerW = g.w - bw - g.safe * 2;
        if (o.hasLogo) {
            e.push({ type: 'logo', x: bw / 2, y: g.h / 2, h: Math.min(bw * 0.62, 14), align: 'center', vcenter: true, onColor: true });
        } else {
            e.push(text(0, g.h / 2 + T.mono * 0.34, bw, initials(d), T.mono,
                { align: 'center', color: 'paper', weight: 700, baseline: true, maxLines: 1, role: 'monogram' }));
        }
        var y = g.sy + T.name * 0.9;
        e.push(text(innerX, y, innerW, d.name, T.nameSm, { weight: 700, baseline: true, maxLines: 2 }));
        if (d.role) e.push(text(innerX, y + T.role * 1.55, innerW, d.role, T.role, { color: 'ink', opacity: 0.62, baseline: true, maxLines: 2 }));
        var rows = contactRows(d);
        var cb = contactBlock(rows, {
            fit: o.fit,
            x: innerX, bottom: g.sy + g.sh,
            w: o.hasQr ? innerW - o.qrSize - 2.5 : innerW, icons: true, size: T.contact * 0.95,
        });
        return e.concat(cb.elements);
    });

    /* 4. Colour band across the top. */
    preset('band-top', ['color'], function (g, d, o) {
        var bh = Math.max(8, g.h * 0.32);
        var e = [rect(0, 0, g.w, bh, 'ink', { bleed: 'ltr' })];
        var ty = bh / 2;
        if (o.hasLogo) {
            e.push({ type: 'logo', x: g.sx, y: ty, h: Math.min(bh * 0.5, 9), align: 'left', vcenter: true, onColor: true });
        }
        e.push(text(o.hasLogo ? g.sx + 14 : g.sx, ty + T.company * 0.36, g.sw - (o.hasLogo ? 14 : 0),
            d.company || d.name, T.company, { color: 'paper', weight: 700, baseline: true, maxLines: 1 }));

        var y = bh + g.safe + T.name * 0.85;
        e.push(text(g.sx, y, g.sw, d.name, T.nameSm, { weight: 700, baseline: true, maxLines: 1 }));
        if (d.role) e.push(text(g.sx, y + T.role * 1.5, g.sw, d.role, T.role, { color: 'ink', opacity: 0.62, baseline: true, maxLines: 1 }));
        var rows = contactRows(d);
        var cb = contactBlock(rows, {
            fit: o.fit,
            x: g.sx, bottom: g.sy + g.sh,
            w: o.hasQr ? g.sw - o.qrSize - 2.5 : g.sw, icons: true, size: T.contact * 0.95,
        });
        return e.concat(cb.elements);
    });

    /* 5. Reversed: the whole card in the ink colour. Handled as a bleed rect so
     *    the background genuinely reaches the page edge — a "full colour" card
     *    whose background stops at the trim is the single most expensive
     *    mistake in this category. */
    preset('solid', ['color', 'bold'], function (g, d, o) {
        var e = [rect(0, 0, g.w, g.h, 'ink', { bleed: 'all' })];
        var y = g.sy + T.name * 0.9;
        if (o.hasLogo) {
            e.push({ type: 'logo', x: g.sx, y: g.sy, h: Math.min(g.sh * 0.26, 10), align: 'left', onColor: true });
            y = g.sy + Math.min(g.sh * 0.26, 10) + 3 + T.name * 0.9;
        }
        e.push(text(g.sx, y, g.sw, d.name, T.name, { weight: 700, color: 'paper', baseline: true, maxLines: 2 }));
        if (d.role) e.push(text(g.sx, y + T.role * 1.55, g.sw, d.role, T.role, { color: 'paper', opacity: 0.72, baseline: true, maxLines: 2 }));
        var rows = contactRows(d);
        var cb = contactBlock(rows, {
            fit: o.fit,
            x: g.sx, bottom: g.sy + g.sh,
            w: o.hasQr ? g.sw - o.qrSize - 2.5 : g.sw, icons: true,
            color: 'paper', iconColor: 'paper', size: T.contact * 0.95,
        });
        return e.concat(cb.elements);
    });

    /* 6. Minimal: one rule, generous air, everything small and precise. */
    preset('minimal', ['simple'], function (g, d, o) {
        var e = [];
        if (o.hasLogo) e.push(cornerLogo(g, o));
        var mid = g.sy + g.sh * 0.44;
        e.push(text(g.sx, mid, g.sw, d.name, T.nameSm, { weight: 500, track: 0.05, baseline: true, maxLines: 1 }));
        if (d.role) e.push(text(g.sx, mid + T.role * 1.7, g.sw, d.role, T.role, { color: 'ink', opacity: 0.62, track: 0.04, baseline: true, maxLines: 2 }));
        e.push(line(g.sx, g.sy + g.sh * 0.62, g.sw, 'muted', 0.2));
        var rows = contactRows(d).filter(function (r) { return !r.wrap; });
        var cb = contactBlock(rows, {
            fit: o.fit,
            x: g.sx, bottom: g.sy + g.sh,
            w: o.hasQr ? g.sw - o.qrSize - 2.5 : g.sw, icons: false, size: T.tiny,
        });
        if (d.company) e.push(text(g.sx, g.sy + T.tiny * 0.85, g.sw, d.company, T.tiny,
            { color: 'accent', weight: 700, track: 0.1, upper: true, baseline: true, maxLines: 2 }));
        return e.concat(cb.elements);
    });

    /* 7. Split: colour on the right third, content on the left. */
    preset('split', ['color', 'logo'], function (g, d, o) {
        var pw = g.w * 0.38;
        var e = [rect(g.w - pw, 0, pw, g.h, 'ink', { bleed: 'rtb' })];
        var innerW = g.w - pw - g.safe * 2;
        if (o.hasLogo) {
            e.push({ type: 'logo', x: g.w - pw / 2, y: g.h / 2, h: Math.min(pw * 0.5, 13), align: 'center', vcenter: true, onColor: true });
        } else {
            e.push(text(g.w - pw, g.h / 2 + T.mono * 0.34, pw, initials(d), T.mono,
                { align: 'center', color: 'paper', weight: 700, baseline: true, maxLines: 1, role: 'monogram' }));
        }
        var y = g.sy + T.name * 0.9;
        e.push(text(g.sx, y, innerW, d.name, T.nameSm, { weight: 700, baseline: true, maxLines: 2 }));
        if (d.role) e.push(text(g.sx, y + T.role * 1.5, innerW, d.role, T.role, { color: 'ink', opacity: 0.62, baseline: true, maxLines: 2 }));
        var rows = contactRows(d);
        var cb = contactBlock(rows, {
            fit: o.fit,
            x: g.sx, bottom: g.sy + g.sh,
            w: innerW, icons: true, size: T.contact * 0.95,
        });
        return e.concat(cb.elements);
    });

    /* 8. Monogram: big initials as the graphic, everything else quiet. */
    preset('monogram', ['bold'], function (g, d, o) {
        var e = [];
        var mSize = Math.min(g.h * 0.46, 18);
        if (o.hasLogo) {
            /* The monogram exists because there is no logo. Given one, the logo
             * takes its place — showing both puts two marks in the same role. */
            e.push({ type: 'logo', x: g.sx, y: g.sy, h: Math.min(mSize * 0.8, 14),
                align: 'left', maxW: g.sw * 0.5 });
        } else {
            e.push(text(g.sx, g.sy + mSize * 0.78, g.sw * 0.5, initials(d), mSize,
                { weight: 700, color: 'accent', baseline: true, maxLines: 1, track: -0.02, role: 'monogram' }));
        }
        var y = g.sy + mSize * 0.78 + T.name * 1.5;
        e.push(text(g.sx, y, g.sw, d.name, T.nameSm, { weight: 700, baseline: true, maxLines: 1 }));
        if (d.role) e.push(text(g.sx, y + T.role * 1.5, g.sw, d.role, T.role, { color: 'ink', opacity: 0.62, baseline: true, maxLines: 1 }));
        var rows = contactRows(d).filter(function (r) { return !r.wrap; });
        var cb = contactBlock(rows, {
            fit: o.fit,
            x: g.sx + g.sw * 0.52, y: g.sy + T.tiny * 0.85,
            w: g.sw * 0.48, icons: false, size: T.tiny, align: 'right',
        });
        return e.concat(cb.elements);
    });

    /* 9. Corner: a diagonal accent wedge. The only preset with a non-rectangular
     *    shape, and it is a bleed element on two edges at once. */
    preset('corner', ['color'], function (g, d, o) {
        var s = Math.min(g.w * 0.42, g.h * 0.9);
        var e = [{ type: 'poly', color: 'accent', bleed: 'lt',
            points: [[0, 0], [s, 0], [0, s * 0.72]] }];
        if (o.hasLogo) e.push(cornerLogo(g, o));
        var y = g.sy + s * 0.42 + T.name * 0.9;
        e.push(text(g.sx, y, o.hasLogo ? g.sw - cornerLogoW(g, o) - 3 : g.sw, d.name, T.name,
            { weight: 700, baseline: true, maxLines: 2 }));
        if (d.role) e.push(text(g.sx, y + T.role * 1.55, g.sw, d.role, T.role, { color: 'ink', opacity: 0.62, baseline: true, maxLines: 1 }));
        if (d.company) e.push(text(g.sx, g.sy + T.tiny * 0.9, g.sw * 0.5, d.company, T.tiny,
            { color: 'paper', weight: 700, upper: true, track: 0.08, baseline: true, maxLines: 2 }));
        var rows = contactRows(d);
        var cb = contactBlock(rows, {
            fit: o.fit,
            x: g.sx, bottom: g.sy + g.sh,
            w: o.hasQr ? g.sw - o.qrSize - 2.5 : g.sw, icons: true, size: T.contact * 0.95,
        });
        return e.concat(cb.elements);
    });

    /* 10. Right-aligned contacts, name left — a two-column reading order that
     *     handles long Russian job titles better than a centred layout. */
    preset('two-column', ['simple'], function (g, d, o) {
        var e = [];
        var colW = g.sw * 0.46;
        if (o.hasLogo) e.push(cornerLogo(g, o));
        var y = g.sy + T.name * 0.9;
        e.push(text(g.sx, y, colW, d.name, T.nameSm, { weight: 700, baseline: true, maxLines: 2 }));
        if (d.role) e.push(text(g.sx, y + T.role * 1.5, colW, d.role, T.role, { color: 'ink', opacity: 0.62, baseline: true, maxLines: 3 }));
        if (d.company) e.push(text(g.sx, g.sy + g.sh, colW, d.company, T.tiny,
            { color: 'accent', weight: 700, upper: true, track: 0.08, anchor: 'bottom', maxLines: 2 }));
        e.push(line(g.sx + g.sw * 0.5, g.sy + g.sh * 0.08, 0, 'muted', 0.2));
        var rows = contactRows(d);
        var cb = contactBlock(rows, {
            fit: o.fit,
            x: g.sx + g.sw * 0.54, y: g.sy + T.contact * 0.85,
            w: g.sw * 0.46, icons: false, size: T.contact * 0.92, align: 'right',
        });
        return e.concat(cb.elements);
    });

    /* 11. Logo-led company card: the logo IS the card, contacts underneath. */
    preset('logo-hero', ['logo', 'simple'], function (g, d, o) {
        var e = [];
        var lh = Math.min(g.sh * 0.42, 15);
        if (o.hasLogo) e.push({ type: 'logo', x: g.sx + g.sw / 2, y: g.sy, h: lh, align: 'center' });
        else e.push(text(g.sx, g.sy + lh * 0.72, g.sw, d.company || d.name, Math.min(lh * 0.6, 6.5),
            { align: 'center', weight: 700, baseline: true, maxLines: 2 }));
        var y = g.sy + lh + 3.2;
        if (d.tagline) { e.push(text(g.sx, y + T.tagline * 0.8, g.sw, d.tagline, T.tagline, { align: 'center', color: 'ink', opacity: 0.62, baseline: true, maxLines: 2 })); y += T.tagline * 2; }
        var rows = contactRows(d);
        var joined = summaryContacts(rows);
        if (joined.length) e.push(text(g.sx, g.sy + g.sh, g.sw, joined.join('   ·   '), T.contact * 0.95,
            { align: 'center', anchor: 'bottom', maxLines: 3, shrinkOnly: true }));
        return e;
    });

    /* 12. Framed: a thin rule inset from the trim. Deliberately inset by more
     *     than the cut tolerance — a frame drawn 1 mm from the edge is the
     *     classic way to make a perfectly good print look crooked, because the
     *     eye reads the varying margin as a misprint. */
    preset('framed', ['classic'], function (g, d, o) {
        var inset = Math.max(g.safe, 3.2);
        var e = [{ type: 'frame', x: inset, y: inset, w: g.w - inset * 2, h: g.h - inset * 2, color: 'accent', stroke: 0.3 }];
        var cx = g.sx;
        var w = g.sw;
        var y = g.sy + g.sh * 0.36;
        if (o.hasLogo) {
            var lh = Math.min(g.sh * 0.22, 8);
            e.push({ type: 'logo', x: g.sx + g.sw / 2, y: y - T.nameSm - lh - 1.5,
                h: lh, align: 'center', maxW: g.sw * 0.5 });
        }
        e.push(text(cx, y, w, d.name, T.nameSm, { weight: 700, align: 'center', baseline: true, maxLines: 2 }));
        if (d.role) e.push(text(cx, y + T.role * 1.6, w, d.role, T.role, { color: 'ink', opacity: 0.62, align: 'center', baseline: true, maxLines: 1 }));
        if (d.company) e.push(text(cx, inset + T.tiny * 2.2, w, d.company, T.tiny,
            { color: 'accent', weight: 700, upper: true, track: 0.1, align: 'center', baseline: true, maxLines: 2 }));
        var rows = contactRows(d);
        var joined = summaryContacts(rows);
        if (joined.length) e.push(text(cx, g.h - inset - T.tiny * 1.3, w, joined.join('   ·   '), T.tiny,
            { align: 'center', anchor: 'bottom', maxLines: 3, shrinkOnly: true }));
        return e;
    });

    /* ── back sides ─────────────────────────────────────────────────────────
     * A back is not a second front. It has one job — usually the logo, a QR, or
     * a slogan — and the commonest professional back is a solid colour with the
     * mark centred. Offering "the same layout again" would be the wrong default
     * and is deliberately not here.
     */
    var BACKS = [
        { id: 'blank', build: function () { return []; } },
        { id: 'solid', build: function (g, d, o) {
            return [rect(0, 0, g.w, g.h, 'ink', { bleed: 'all' })];
        } },
        { id: 'logo', build: function (g, d, o) {
            var e = [rect(0, 0, g.w, g.h, 'ink', { bleed: 'all' })];
            if (o.hasLogo) e.push({ type: 'logo', x: g.w / 2, y: g.h / 2, h: Math.min(g.h * 0.34, 13), align: 'center', vcenter: true, onColor: true });
            else e.push(text(g.sx, g.h / 2 + T.mono * 0.34, g.sw, initials(d), T.mono,
                { align: 'center', color: 'paper', weight: 700, baseline: true, maxLines: 1, role: 'monogram' }));
            if (d.tagline) e.push(text(g.sx, g.h / 2 + Math.min(g.h * 0.34, 13) * 0.5 + T.tagline * 1.8, g.sw,
                d.tagline, T.tagline, { align: 'center', color: 'paper', opacity: 0.72, baseline: true, maxLines: 2 }));
            return e;
        } },
        { id: 'qr', build: function (g, d, o) {
            var e = [];
            var s = Math.min(g.sh * 0.86, g.sw * 0.5);
            e.push({ type: 'qr', x: g.w / 2 - s / 2, y: g.sy + (g.sh - s) / 2, size: s });
            if (d.tagline || d.site) {
                e.push(text(g.sx, g.sy + g.sh, g.sw, d.tagline || d.site, T.tiny,
                    { align: 'center', color: 'ink', opacity: 0.62, baseline: true, maxLines: 1 }));
            }
            return e;
        } },
        { id: 'tagline', build: function (g, d, o) {
            var e = [rect(0, 0, g.w, g.h, 'accent', { bleed: 'all' })];
            e.push(text(g.sx, g.h / 2 + T.tagline * 0.9, g.sw, d.tagline || d.company || '', T.tagline * 1.5,
                { align: 'center', color: 'paper', weight: 700, baseline: true, maxLines: 3 }));
            return e;
        } },
    ];

    function all() { return LIST.slice(); }
    function byId(id) {
        for (var i = 0; i < LIST.length; i++) if (LIST[i].id === id) return LIST[i];
        return LIST[0];
    }
    function backById(id) {
        for (var i = 0; i < BACKS.length; i++) if (BACKS[i].id === id) return BACKS[i];
        return BACKS[0];
    }
    function backs() { return BACKS.slice(); }

    return {
        T: T,
        ICONS: ICONS,
        ICON_STROKE: ICON_STROKE,
        all: all,
        byId: byId,
        backs: backs,
        backById: backById,
        contactRows: contactRows,
        initials: initials,
    };
});
